package com.example.mystybag.logoutUtils.helpers

import dev.icerock.moko.resources.desc.StringDesc
import com.example.mystybag.communication.Communication
import com.example.mystybag.core.defines.UtilsPreferencesDefines.PREFERENCES_IS_STAFF
import com.example.mystybag.core.defines.UtilsPreferencesDefines.PREFERENCES_MERCHANT_ID
import com.example.mystybag.core.defines.UtilsPreferencesDefines.PREFERENCES_MERCHANT_JWT
import com.example.mystybag.core.defines.UtilsPreferencesDefines.PREFERENCES_PUSH_TOKEN
import com.example.mystybag.core.defines.UtilsPreferencesDefines.PREFERENCES_STAFF_CAN_CREATE_BAGS
import com.example.mystybag.core.defines.UtilsPreferencesDefines.PREFERENCES_STAFF_CAN_VIEW_REVENUE
import com.example.mystybag.core.defines.UtilsPreferencesDefines.PREFERENCES_STAFF_LOCATION_IDS
import com.example.mystybag.biometricUtils.expected.BiometricsHandler
import com.example.mystybag.core.defines.UtilsWalletDefines.FINGERPRINT_KEY_NAME
import com.example.mystybag.core.defines.UtilsWalletDefines.ONE_MINUTE_IN_MILLISECONDS
import com.example.mystybag.core.defines.UtilsWalletDefines.TEN_MINUTES_IN_MILLISECONDS
import com.example.mystybag.core.expected.LocaleHelper.updateLocaleAndShortcuts
import com.example.mystybag.core.expected.LogHelper
import com.example.mystybag.core.expected.Preferences
import com.example.mystybag.core.expected.Preferences.setString
import com.example.mystybag.core.expected.getLanguage
import com.example.mystybag.core.helpers.GlobalCommonData
import com.example.mystybag.core.helpers.ShortcutsProviderInterface
import com.example.mystybag.core.helpers.globalExceptionHandler
import com.example.mystybag.core.logic.base.AppViewModel
import com.example.mystybag.core.security.Security
import com.example.mystybag.database.communication.db.DatabaseProvider
import com.example.mystybag.common.expected.DarkThemeHelper.isDarkThemed
import com.example.mystybag.remoteLogger.RemoteLogger
import com.example.mystybag.sharedmodels.ModelsData
import kotlinx.coroutines.CoroutineName
import kotlinx.coroutines.CoroutineScope
import kotlinx.coroutines.Dispatchers
import kotlinx.coroutines.channels.Channel
import kotlinx.coroutines.channels.ReceiveChannel
import kotlinx.coroutines.launch
import kotlinx.coroutines.plus
import kotlinx.datetime.Clock

object LogoutMethods {

    private var lastPausedTimestamp = 0L
    private val logoutChannel_      = Channel<Long>()
    val channel                     : ReceiveChannel<Long> = logoutChannel_

    /** Извиква се при връщане от бекграунд след ≥10 мин, ако потребителят е бил логнат като служител или търговец – след изчистване на сесията. Параметърът е true за staff, false за merchant. */
    var onStaffOrMerchantBackgroundTimeout: ((isStaff: Boolean) -> Unit)? = null

    fun getShouldPreventConnecting(): Boolean =  (!GlobalCommonData.getIsApplicationOnForeground() && getHasToLogOutDueToInactivity())

    fun getHasToLogOutDueToInactivity(milliseconds: Int = ONE_MINUTE_IN_MILLISECONDS): Boolean {
        return (lastPausedTimestamp > 0L) && (Clock.System.now().toEpochMilliseconds() - lastPausedTimestamp > milliseconds)
    }

    fun setAppOnForeground() {
        LogHelper.logEvent("App on foreground")
        LogHelper.logAnalyticsEvent("app_foreground")
        GlobalCommonData.setApplicationOnForeground(true)
        val duration = if (lastPausedTimestamp > 0L) Clock.System.now().toEpochMilliseconds() - lastPausedTimestamp else 0L

        // Ако е логнат като служител/търговец и апът е бил на бекграунд ≥10 мин – изчистваме сесията и отваряме екрана за логин
        if (duration >= TEN_MINUTES_IN_MILLISECONDS) {
            val merchantJwt = Preferences.getString(PREFERENCES_MERCHANT_JWT, "")
            val isStaff = Preferences.getBoolean(PREFERENCES_IS_STAFF, false)
            if (merchantJwt.isNotEmpty()) {
                Communication.token = ""
                setString(PREFERENCES_MERCHANT_JWT, "")
                Preferences.setLong(PREFERENCES_MERCHANT_ID, 0L)
                Preferences.setBoolean(PREFERENCES_IS_STAFF, false)
                Preferences.setString(PREFERENCES_STAFF_LOCATION_IDS, "")
                Preferences.setBoolean(PREFERENCES_STAFF_CAN_CREATE_BAGS, false)
                Preferences.setBoolean(PREFERENCES_STAFF_CAN_VIEW_REVENUE, false)
                onStaffOrMerchantBackgroundTimeout?.invoke(isStaff)
            }
        }

        if (duration > TEN_MINUTES_IN_MILLISECONDS) {
            (CoroutineScope(Dispatchers.Default) + globalExceptionHandler).launch(CoroutineName("setAppOnForeground")) {
                logoutChannel_.send(duration)
            }
        }
        lastPausedTimestamp = 0L
    }

    fun setAppOnBackground() {
        LogHelper.logEvent("App on background")
        LogHelper.logAnalyticsEvent("app_background")
        GlobalCommonData.setApplicationOnForeground(false)
        lastPausedTimestamp = Clock.System.now().toEpochMilliseconds()
    }

    private fun reset() {
        lastPausedTimestamp = 0
    }

    fun logout() {
        reset()
        Communication.isLoginSuccess = false
        Communication.token = ""
        setString(PREFERENCES_MERCHANT_JWT, "")
    }

    fun deleteAppDataAndGoToEntryScreen(msg: String) {
        deleteAppData()
        logout()
    }

    private fun deleteAppData() {
        val token = Preferences.getString(PREFERENCES_PUSH_TOKEN, "")
        Preferences.clear()
        StringDesc.localeType = StringDesc.LocaleType.System
        getLanguage().also { language ->
            ModelsData.language = language
            updateLocaleAndShortcuts(language)
        }

        BiometricsHandler.deleteKeyFromKeyStore(FINGERPRINT_KEY_NAME)
        GlobalCommonData.resetData()
        ShortcutsProviderInterface.shortcutProvider?.clearShortcuts()

        (CoroutineScope(LogoutDispatchers.IO) + globalExceptionHandler).launch(CoroutineName("deleteAppData")) {
            DatabaseProvider.clearDatabase()
        }
        setString(PREFERENCES_PUSH_TOKEN, token)

        AppViewModel.setDarkTheme(false)
        isDarkThemed(false)
        Security.checkKeys()
        RemoteLogger.walletID = null
        RemoteLogger.enabled = false
        RemoteLogger.resourceID = "-"
    }
}